import React, { useState, useRef, useEffect } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { Row, Col } from '@ui/wrapper'
import Button from '@ui/button'
import { useMount } from '@hooks'
import { StyledAccordion, StyledAccordionItem, RowMarginBottom } from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf'
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown'
import { faAngleUp } from '@fortawesome/pro-solid-svg-icons/faAngleUp'
import { usePageContext } from '@context/PageContext'

const AccordionWrap = ({
  defaultActiveKey,
  data,
  layout = 1,
  disableDefaultActive,
  iconAtEnd,
  onSelect,
  activeKey,
  colBreakpoints = { lg: 6 },
  maxItems,
  showItemCount = false,
  ...props
}) => {
  const [showingMore, setShowingMore] = useState(false)
  const [activeAccordionKey, setActiveAccordionKey] = useState(
    defaultActiveKey || 0
  )
  const itemRefs = useRef([])
  const accordionRef = useRef(null)
  const mounted = useMount()

  const { locale } = usePageContext()

  const texts = {
    showMore: {
      en: 'Show more',
      de: 'Mehr anzeigen'
    },
    showLess: {
      en: 'Show less',
      de: 'Weniger anzeigen'
    }
  }

  const itemsToList = maxItems && !showingMore ? data.slice(0, maxItems) : data

  const scrollToItem = (id) => {
    const itemRef = itemRefs.current[id]
    if (itemRef && itemRef.getBoundingClientRect().top < 100) {
      itemRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleSelect = (key) => {
    setActiveAccordionKey(key)
    if (onSelect) {
      onSelect(key)
    }
  }

  useEffect(() => {
    if (mounted && !showingMore) {
      accordionRef?.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [showingMore])

  return (
    <StyledAccordion layout={layout} ref={accordionRef} {...props}>
      <Accordion
        defaultActiveKey={
          disableDefaultActive || defaultActiveKey || data[0].id
        }
        onSelect={handleSelect}
        activeKey={activeKey}
      >
        {itemsToList?.map((item) => (
          <StyledAccordionItem
            key={item.id}
            backgroundColor={item.color?.replace(',1)', ',0.1)')}
            color={item.color}
            ref={(el) => (itemRefs.current[item.id] = el)}
          >
            <Accordion.Item key={item.id} eventKey={item.id}>
              <Accordion.Header>
                {item.title +
                  (showItemCount ? ` - (${item.texts?.length || 0})` : '')}
                <span
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      activeAccordionKey === item.id ? faAngleUp : faAngleDown
                    }
                  />
                </span>
              </Accordion.Header>
              <Accordion.Body onEntered={() => scrollToItem(item.id)}>
                <RowMarginBottom>
                  {item.texts ? (
                    item.texts.map((t, index) => {
                      if (t.content.includes('~')) {
                        const leftText = t.content.split('~')[0]
                        const rightText = t.content.split('~')[1]
                        return (
                          <Row key={index} className="row">
                            <Col {...colBreakpoints}>
                              <div
                                dangerouslySetInnerHTML={{ __html: leftText }}
                              />
                            </Col>
                            <Col {...colBreakpoints}>
                              <div
                                dangerouslySetInnerHTML={{ __html: rightText }}
                              />
                            </Col>
                          </Row>
                        )
                      } else {
                        const tArr = t.content.split('|')
                        const content = tArr.length === 2 ? tArr[1] : tArr[0]
                        if (tArr[0] === 'title') {
                          return (
                            <b key={t.id} style={{ color: item.color }}>
                              {index !== 0 && <br />}
                              {content}
                              <br />
                            </b>
                          )
                        } else if (tArr[0] === 'text') {
                          return (
                            <React.Fragment key={t.id}>
                              {index !== 0 && <br />}
                              <span
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                              {index !== item.texts.length - 1 && <br />}
                            </React.Fragment>
                          )
                        } else {
                          const icon = (
                            <FontAwesomeIcon
                              icon={faFilePdf}
                              style={{
                                marginRight: '0.5em',
                                marginTop: '0.3em'
                              }}
                            />
                          )
                          return (
                            <div key={t.id} style={{ display: 'flex' }}>
                              {!iconAtEnd && icon}
                              &nbsp;
                              {t.media?.localFile?.publicURL || t.path ? (
                                <a
                                  href={t.media?.localFile?.publicURL || t.path}
                                  target="_blank"
                                  style={{ display: 'inline' }}
                                  rel="noreferrer"
                                >
                                  {content}
                                </a>
                              ) : (
                                <span>{content}</span>
                              )}
                              {iconAtEnd && icon}
                              {index !== item.texts.length - 1 && <br />}
                            </div>
                          )
                        }
                      }
                    })
                  ) : (
                    <span>
                      {!iconAtEnd && (
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          style={{ marginRight: '0.5em', marginTop: '0.3em' }}
                        />
                      )}
                      {item.description}
                      {iconAtEnd && (
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          style={{ marginRight: '0.5em', marginTop: '0.3em' }}
                        />
                      )}
                    </span>
                  )}
                </RowMarginBottom>
              </Accordion.Body>
            </Accordion.Item>
          </StyledAccordionItem>
        ))}
      </Accordion>
      {maxItems && data.length > maxItems && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button
            key={showingMore ? 'less' : 'more'}
            type="button"
            variant="texted"
            size="large"
            fullwidth
            onClick={() => {
              setShowingMore((prev) => !prev)
            }}
            style={{ textDecoration: 'none' }}
          >
            {showingMore ? texts.showLess[locale] : texts.showMore[locale]}
          </Button>
        </div>
      )}
    </StyledAccordion>
  )
}

export default AccordionWrap
